import { useState } from 'react';
import type { FormEvent } from 'react';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/dahgobbp14a1h3mr6m9g';

type Status = 'idle' | 'loading' | 'success' | 'error';

export default function NewsletterForm() {
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const payload = new FormData(form);

    const honeypot = String(payload.get('website_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      setFormError('');
      form.reset();
      return;
    }
    payload.delete('website_alt');

    const body = new URLSearchParams();
    payload.forEach((value, key) => {
      body.append(key, String(value));
    });

    setStatus('loading');
    setFormError('');

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || responseText || '';
      const isSpam = serverMsg.toLowerCase().includes('spam');
      const isOk = response.ok && parsed?.code === 'OK';

      if (isOk && !isSpam) {
        setStatus('success');
        setFormError('');
        form.reset();
      } else {
        setStatus('error');
        setFormError(serverMsg || '提交失败，请稍后再试。');
      }
    } catch {
      setStatus('error');
      setFormError('网络开小差了，请稍后再试。');
    }
  };

  return (
    <form
      data-readdy-form
      onSubmit={handleSubmit}
      className="w-full"
      aria-label="订阅吃瓜速递"
    >
      <div className="flex flex-col gap-2 sm:flex-row">
        <input
          type="text"
          name="nickname"
          autoComplete="nickname"
          placeholder="怎么称呼你（可留空）"
          className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200 sm:w-40"
        />
        <input
          type="email"
          name="email"
          required
          autoComplete="email"
          placeholder="你的邮箱地址"
          className="w-full flex-1 rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
        />
        <input
          type="text"
          name="website_alt"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
          className="form-extra-field"
        />
        <button
          type="submit"
          disabled={status === 'loading'}
          className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
        >
          {status === 'loading' ? '订阅中…' : '订阅吃瓜速递'}
        </button>
      </div>

      {status === 'success' && (
        <p className="mt-3 flex items-center gap-1.5 text-sm text-secondary-700">
          <i className="ri-checkbox-circle-line" />
          订阅成功，热瓜有新进展会第一时间通知你。
        </p>
      )}
      {status === 'error' && (
        <p className="mt-3 flex items-center gap-1.5 text-sm text-primary-700">
          <i className="ri-error-warning-line" />
          {formError}
        </p>
      )}
      <p className="mt-3 text-xs text-foreground-500">
        我们只会发送瓜料更新提醒，不会外传你的邮箱。
      </p>
    </form>
  );
}